import { useState } from "react";
import { DetailHeader } from "@/components/layout/detail-header";
import { Button } from "@/components/ui/button";
import { FileText, Database, Play, BarChart, Share } from "lucide-react";

interface HelpSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  subsections?: HelpSubsection[];
}

interface HelpSubsection {
  id: string;
  title: string;
}

const helpSections: HelpSection[] = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: <Play className="w-4 h-4" />
  },
  {
    id: "data-handling",
    title: "Data Handling",
    icon: <Database className="w-4 h-4" />
  },
  {
    id: "test-steps",
    title: "Test Steps",
    icon: <FileText className="w-4 h-4" />,
    subsections: [
      { id: "initial-steps", title: "Initial Steps" },
      { id: "load-steps", title: "Load Steps" },
      { id: "filter-steps", title: "Filter Steps" },
      { id: "tool-steps", title: "Tool Steps" }
    ]
  },
  {
    id: "running-tests",
    title: "Running Tests",
    icon: <Play className="w-4 h-4" />
  },
  {
    id: "analyzing-results",
    title: "Analyzing Results",
    icon: <BarChart className="w-4 h-4" />
  },
  {
    id: "sharing-tests",
    title: "Sharing Tests",
    icon: <Share className="w-4 h-4" />
  }
];

export function HelpPage() {
  const [activeSection, setActiveSection] = useState("getting-started");

  const renderContent = () => {
    switch (activeSection) {
      case "getting-started":
        return (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Getting Started</h1>
            <p className="text-muted-foreground">
              The Agent Test Management system helps you create, manage, and run tests for Flatfile Agent transformations. Here's how to get started:
            </p>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Create a new test using the "New Test" button</li>
              <li>Add an API Token step to your test</li>
              <li>Configure your test with steps and expectations</li>
              <li>Run your tests and analyze results</li>
            </ol>
          </div>
        );

      case "data-handling":
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Data Handling</h1>
            <p className="text-muted-foreground">
              Test steps handle data in three different ways:
            </p>
            
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">1. Previous Result Data</h3>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Data passed directly from the previous step to the next step</li>
                  <li>• Typically includes IDs for sheets, spaces, files, and workbooks</li>
                  <li>• Used for immediate next-step operations</li>
                  <li>• Example: Filter steps use IDs from previous load steps</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">2. Global Run Data</h3>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Persistent data store available throughout the test run</li>
                  <li>• Initial steps store important IDs here for later use</li>
                  <li>• Can be accessed by any step at any time</li>
                  <li>• Example: Space ID stored by initial step used later for file uploads</li>
                </ul>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="font-semibold mb-2">3. Step Data</h3>
                <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                  <li>• Configuration data defined during step setup</li>
                  <li>• Includes parameters like names, slugs, or specific IDs</li>
                  <li>• Remains constant throughout test runs</li>
                  <li>• Example: Filter criteria or job configurations</li>
                </ul>
              </div>
            </div>

            <div className="mt-6">
              <h3 className="font-semibold mb-3">Example Data Flow</h3>
              <div className="bg-muted p-4 rounded-lg font-mono text-sm">
                <pre className="whitespace-pre-wrap">
{`[1] Load Space by Namespace
    ↓ Returns: spaceId
    ↓ Stores to Global: spaceId
    ↓
[2] Load All Data from Space
    ← Uses Global: spaceId
    ↓ Returns: workbookIds, fileIds, sheetIds
    ↓
[3] Filter Sheet by Name "test_sheet"
    ← Uses Previous: sheetIds
    ↓ Returns: filtered sheetId
    ↓
[4] Store Data to Global
    ← Uses Previous: sheetId
    ↓ Stores to Global: destinationSheetId
    ↓
[5] Upload File to Space
    ← Uses Global: spaceId
    ↓ Returns: fileId, workbookId, sheetId
    ↓
[6] AI Transform
    ← Uses Previous: sheetId (source)
    ← Uses Global: destinationSheetId`}
                </pre>
              </div>
            </div>
          </div>
        );

      case "initial-steps":
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Initial Steps</h1>
            <p className="text-muted-foreground">
              These steps must be used as the first step in a workflow or can be used later. They load or create data and store it in the global run data store.
            </p>
            
            <div className="space-y-4">
              {[
                {
                  title: "Spaces - Create",
                  description: "Creates a space in Flatfile, waits for space:configure job to execute, stores SpaceID, WorkbookIDs, and SheetIds to global run data",
                  fields: "namespace, spaceName"
                },
                {
                  title: "Spaces - Load by ID", 
                  description: "Loads a space from Flatfile using space ID, stores SpaceID to global run data",
                  fields: "spaceIds (comma-separated)"
                },
                {
                  title: "Spaces - Load by Namespace",
                  description: "Loads all spaces based on namespace, supports wildcard (*) in namespace, stores SpaceID to global run data",
                  fields: "namespace"
                },
                {
                  title: "Workbooks - Load by ID",
                  description: "Loads specific workbooks, stores workbookId and spaceId to global run data", 
                  fields: "workbookIds (comma-separated)"
                },
                {
                  title: "Sheets - Load by ID",
                  description: "Loads specific sheets, stores sheetId, workbookId, and spaceId to global run data",
                  fields: "sheetIds (comma-separated)"
                },
                {
                  title: "Files - Load by ID",
                  description: "Loads specific files, stores fileId and related IDs to global run data",
                  fields: "fileIds (comma-separated)"
                }
              ].map((step) => (
                <div key={step.title} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                  <div className="text-sm">
                    <span className="font-medium">Required fields: </span>
                    <span className="text-muted-foreground">{step.fields}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "load-steps":
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Load Steps</h1>
            <p className="text-muted-foreground">
              These steps handle data loading operations and require previous steps to provide necessary IDs.
            </p>
            
            <div className="space-y-4">
              {[
                {
                  title: "Spaces - Load Data",
                  description: "Loads all files, workbooks, and sheets from spaces. Requires space IDs from previous steps. Can be filtered using subsequent filter steps.",
                  fields: "dataLoadOptions (loadFiles, loadWorkbooks, loadSheets)"
                },
                {
                  title: "Files - Upload to Space",
                  description: "Uploads files to a specified space. Sets global data with uploaded files, workbooks, and sheets. Can be filtered using subsequent filter steps.",
                  fields: "uploadFiles"
                }
              ].map((step) => (
                <div key={step.title} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                  <div className="text-sm">
                    <span className="font-medium">Required fields: </span>
                    <span className="text-muted-foreground">{step.fields}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "filter-steps":
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Filter Steps</h1>
            <p className="text-muted-foreground">
              These steps filter data loaded by previous steps.
            </p>
            
            <div className="space-y-4">
              {[
                {
                  title: "Workbooks - Filter by Name",
                  description: "Filters workbooks by name/slug. Supports wildcards.",
                  fields: "workbookSlugs (comma-separated)"
                },
                {
                  title: "Sheets - Filter by Name/Slug",
                  description: "Filters sheets by name/slug. Supports wildcards.",
                  fields: "sheetSlugs (comma-separated)"
                },
                {
                  title: "Files - Load by Name",
                  description: "Filters files by name. Supports wildcards.",
                  fields: "fileNames (comma-separated)"
                },
                {
                  title: "Spaces - Load by Name",
                  description: "Filters spaces by name. Supports wildcards.",
                  fields: "spaceNames (comma-separated)"
                },
                {
                  title: "Spaces - Delete",
                  description: "Deletes spaces created during test. Removes all associated workbooks and sheets.",
                  fields: "No additional fields required"
                }
              ].map((step) => (
                <div key={step.title} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                  <div className="text-sm">
                    <span className="font-medium">Required fields: </span>
                    <span className="text-muted-foreground">{step.fields}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "tool-steps":
        return (
          <div className="space-y-6">
            <h1 className="text-2xl font-bold">Tool Steps</h1>
            <p className="text-muted-foreground">
              These steps provide various utilities and validation capabilities.
            </p>
            
            <div className="space-y-4">
              {[
                {
                  title: "Evaluate Expectations for Records",
                  description: "Validates record-level expectations. Requires sheet/workbook data from previous steps.",
                  fields: "expectations (selected from test expectations)"
                },
                {
                  title: "Evaluate Expectations for Sheet Configuration", 
                  description: "Validates sheet-level configuration. Requires sheet data from previous steps.",
                  fields: "expectations (selected from test expectations)"
                },
                {
                  title: "Evaluate Expectations for Workbook Configuration",
                  description: "Validates workbook-level configuration. Requires workbook data from previous steps.", 
                  fields: "expectations (selected from test expectations)"
                },
                {
                  title: "Evaluate Expectations for Mapping Rules",
                  description: "Validates mapping rules. Requires mapping rules data from previous steps.",
                  fields: "expectations (selected from test expectations)"
                },
                {
                  title: "Store to Global Data",
                  description: "Stores data in the global run data store. Supports template variables ({{sheetId}}, {{workbookId}}, {{spaceId}}, {{fileId}}).",
                  fields: "storeToGlobalData (key-value pairs)"
                },
                {
                  title: "AI Transformation - Prepare Sheet",
                  description: "Prepares and runs AI-generated mapping programs. Operates on all sheets from previous steps. Requires destinationSheetId to be set in global data store (use Store to Global Data step).",
                  fields: "No additional fields required"
                },
                {
                  title: "Jobs - Create and Execute",
                  description: "Creates and executes Flatfile jobs. Waits for job completion.",
                  fields: "jobType, jobSource, jobDestination, jobOperation, jobConfig, jobBody"
                },
                {
                  title: "AI Transformation - Execute Prompt",
                  description: "Executes AI transformation prompts.",
                  fields: "prompt (transformation instructions)"
                }
              ].map((step) => (
                <div key={step.title} className="border rounded-lg p-4">
                  <h3 className="font-semibold mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                  <div className="text-sm">
                    <span className="font-medium">Required fields: </span>
                    <span className="text-muted-foreground">{step.fields}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case "running-tests":
        return (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Running Tests</h1>
            <ol className="list-decimal list-inside space-y-2 ml-4">
              <li>Navigate to the Runs tab</li>
              <li>Click "New Run"</li>
              <li>Choose the number of runs for batch testing</li>
              <li>Monitor progress in real-time</li>
              <li>View detailed logs for troubleshooting</li>
            </ol>
          </div>
        );

      case "analyzing-results":
        return (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Analyzing Results</h1>
            <p className="text-muted-foreground mb-4">
              After test execution, you can:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Review run groups and individual runs</li>
              <li>Examine step-by-step execution</li>
              <li>View validation results</li>
              <li>Generate reports and sheets for analysis</li>
            </ul>
          </div>
        );

      case "sharing-tests":
        return (
          <div className="space-y-4">
            <h1 className="text-2xl font-bold">Sharing Tests</h1>
            <p className="text-muted-foreground mb-4">
              You can share tests with others by:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Using the Download button to export a test</li>
              <li>Using the Import button to import tests</li>
              <li>Tests include all configurations, steps, and optionally expectations</li>
            </ul>
          </div>
        );

      default:
        return (
          <div>
            <h1 className="text-2xl font-bold">Help</h1>
            <p>Select a topic from the navigation to get started.</p>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header spanning both sides */}
      <DetailHeader
        breadcrumb={[
          { label: "Help & Documentation" }
        ]}
      />

      <div className="flex flex-1 min-h-0">
        {/* Left Navigation */}
        <div className="w-64 bg-background border-r border-border flex-shrink-0">
          <div className="p-4">
            <nav className="space-y-2">
              {helpSections.map((section) => (
                <div key={section.id}>
                  {section.subsections ? (
                    // Non-clickable header for sections with subsections
                    <div className="px-3 py-2 text-sm font-medium text-muted-foreground">
                      <div className="flex items-center">
                        {section.icon}
                        <span className="ml-2">{section.title}</span>
                      </div>
                    </div>
                  ) : (
                    // Clickable item for sections without subsections
                    <Button
                      variant={activeSection === section.id ? "secondary" : "ghost"}
                      className="w-full justify-start"
                      onClick={() => setActiveSection(section.id)}
                    >
                      {section.icon}
                      <span className="ml-2">{section.title}</span>
                    </Button>
                  )}
                  
                  {/* Always show subsections if they exist */}
                  {section.subsections && (
                    <div className="ml-6 mt-1 space-y-1">
                      {section.subsections.map((subsection) => (
                        <Button
                          key={subsection.id}
                          variant={activeSection === subsection.id ? "secondary" : "ghost"}
                          size="sm"
                          className="w-full justify-start text-sm"
                          onClick={() => setActiveSection(subsection.id)}
                        >
                          {subsection.title}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </nav>
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
